import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminShop, errMsg } from "../../lib/shopApi";
import { getStoryBarnItems } from "../../lib/api";
import { SIZE_GUIDES } from "../../lib/sizeGuides";
import { Field, inputCls, btn } from "./shared";
import { PhotoManager } from "./PhotoManager";
import { VariantTable } from "./VariantTable";

const STATUS_HELP = {
  draft: "Hidden from the website.",
  coming_soon: "Shows on the Story Barn with a Coming Soon badge — can't be bought yet.",
  live: "Customers can buy it.",
};

const PM_FIELDS = [["us_first", "US · first item"], ["us_additional", "US · each extra"], ["ca_first", "Canada · first item"], ["ca_additional", "Canada · each extra"]];

export const ProductEditor = ({ product, passcode, onSaved, onDeleted }) => {
  const [d, setD] = useState(product);
  const [saving, setSaving] = useState(false);
  const [barnItems, setBarnItems] = useState([]);
  useEffect(() => { getStoryBarnItems().then(setBarnItems).catch(() => {}); }, []);
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  const isPM = d.supplier === "printed_mint";

  const save = async (override = {}) => {
    const body = { ...d, ...override, order: Number(d.order) || 100 };
    if (body.status === "live" && product.status !== "live" &&
      !window.confirm(`Put "${d.title}" on sale? Customers will be able to buy it.`)) return;
    setSaving(true);
    try {
      const saved = await adminShop.updateProduct(product.id, body, passcode);
      setD(saved);
      toast("Saved.");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Couldn't save.")); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${d.title}" from the shop? (Printful is not affected.)`)) return;
    await adminShop.deleteProduct(product.id, passcode);
    toast("Deleted.");
    onDeleted();
  };

  return (
    <div className="border-t border-[#E7DFD5] p-5 space-y-5" data-testid={`admin-shop-editor-${product.slug}`}>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Visibility" hint={STATUS_HELP[d.status]}>
          <select className={inputCls} value={d.status} onChange={set("status")} data-testid="admin-shop-edit-status">
            <option value="draft">Draft (hidden)</option>
            <option value="coming_soon">Coming Soon</option>
            <option value="live">Live — for sale</option>
          </select>
        </Field>
        <Field label="Title"><input className={inputCls} value={d.title} onChange={set("title")} data-testid="admin-shop-edit-title" /></Field>
        <Field label="Small label above title" hint="e.g. Shirt, Greeting Card, Placemats"><input className={inputCls} value={d.kind} onChange={set("kind")} data-testid="admin-shop-edit-kind" /></Field>
      </div>
      <Field label="Description"><textarea rows={4} className={inputCls} value={d.description} onChange={set("description")} data-testid="admin-shop-edit-description" /></Field>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Sales tax type">
          <select className={inputCls} value={d.tax_category} onChange={set("tax_category")} data-testid="admin-shop-edit-tax">
            <option value="clothing">Clothing (shirts, apparel)</option>
            <option value="general">General goods (cards, placemats…)</option>
          </select>
        </Field>
        <Field label="Shelf order" hint="Lower numbers show first"><input type="number" className={inputCls} value={d.order} onChange={set("order")} data-testid="admin-shop-edit-order" /></Field>
        <Field label="Made by"><p className="text-sm py-2 text-[#4B3A2F]">{isPM ? "Printed Mint (you place orders)" : "Printful (automatic)"}</p></Field>
        <Field label="Story Barn collection" hint="Which merchandise card it lives inside">
          <select className={inputCls} value={d.collection || ""} onChange={set("collection")} data-testid="admin-shop-edit-collection">
            <option value="">Its own card</option>
            {barnItems.filter((b) => !b.href && b.slug !== "audiobook" && !b.slug.includes("coloring")).map((b) => <option key={b.slug} value={b.slug}>{b.title}</option>)}
          </select>
        </Field>
        <Field label="Size guide" hint="Shown under the size buttons">
          <select className={inputCls} value={d.size_guide || ""} onChange={set("size_guide")} data-testid="admin-shop-edit-size-guide">
            <option value="">None</option>
            {Object.entries(SIZE_GUIDES).map(([k, g]) => <option key={k} value={k}>{g.name}</option>)}
          </select>
        </Field>
      </div>
      <PhotoManager product={d} setProduct={setD} passcode={passcode} />
      <VariantTable variants={d.variants} onChange={(variants) => setD({ ...d, variants })} manual={isPM} />
      {isPM && (
        <div className="grid md:grid-cols-4 gap-3" data-testid="admin-shop-pm-shipping">
          {PM_FIELDS.map(([k, label]) => (
            <Field key={k} label={label}>
              <input type="number" step="0.01" className={inputCls} value={d.pm_shipping?.[k] ?? 0} data-testid={`admin-shop-pm-${k}`}
                onChange={(e) => setD({ ...d, pm_shipping: { ...d.pm_shipping, [k]: Number(e.target.value) } })} />
            </Field>
          ))}
          <div className="md:col-span-4"><Field label="Printed Mint notes (product name / SKU to order)"><input className={inputCls} value={d.pm_notes || ""} onChange={set("pm_notes")} data-testid="admin-shop-pm-notes" /></Field></div>
        </div>
      )}
      <div className="flex flex-wrap gap-3 pt-2">
        <button type="button" className={btn.primary} onClick={() => save()} disabled={saving} data-testid="admin-shop-edit-save">{saving ? "Saving…" : "Save changes"}</button>
        <button type="button" className={btn.danger} onClick={remove} data-testid="admin-shop-edit-delete">Delete</button>
      </div>
    </div>
  );
};
